import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, TextField, MenuItem, Button, Paper } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import 'moment/locale/es';

import * as actions from '../../store/actions/index';
import ConteinerWithEjecutarPagina from '../../components/UI/ConteinerWithEjecutarPagina/ConteinerWithEjecutarPagina';
import ComprobantesImpresionTable from '../../components/Impresion/ComprobantesImpresionTable/ComprobantesImpresionTable';
import UltimaAccionImpresion from '../../components/Impresion/UltimaAccionImpresion/UltimaAccionImpresion';

const useStyles = makeStyles((theme) => ({
  filtros: {
    padding: '10px',
    marginBottom: '10px',
    backgroundColor: 'white',
    border: '1px solid #d9d9d9',
    borderRadius: '3px',
    boxShadow: '0px 3px 6px #00000029'
  },
  campo: {
    width: '100%'
  },
  boton: {
    height: '100%',
    // En tablet y celular el botón queda solo en su fila
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));

/** Anulado (del Eiffel) y lo último que se hizo con el comprobante desde Impresión. */
const Estado = ({ comprobante }) => (
  <React.Fragment>
    {comprobante.anulado && <div style={{ fontWeight: 'bold' }}>Anulado</div>}
    <UltimaAccionImpresion ultimaAccion={comprobante.ultimaAccion} />
  </React.Fragment>
);

const filtrosIniciales = () => ({
  fechaDesde: moment().startOf('month'),
  fechaHasta: moment(),
  cliente: '',
  idComproba: '',
  sucursalId: '',
  nroExterno: ''
});

// Cada circuito es una página del menú: Ventas › Ver comprobantes y Compras › Ver comprobantes.
const circuitos = {
  V: { grupo: 'Ventas', nombre: 'Ver comprobantes', tercero: 'Cliente' },
  C: { grupo: 'Compras', nombre: 'Ver comprobantes', tercero: 'Proveedor' }
};

/**
 * Ver comprobantes (sección 46, impresion): busca comprobantes de ventas (origen 'V') o de compras (origen 'C') del Eiffel y muestra
 * el PDF que genera el servidor del cliente con el mismo formulario que usa el Eiffel para el envío por mail.
 * Las dos páginas usan la misma sección (impresion); el route las monta con key distinta para que no compartan estado.
 */
const Impresion = (props) => {
  const origen = props.origen === 'C' ? 'C' : 'V';
  const circuito = circuitos[origen];
  sessionStorage.setItem('seccionNombre', `${circuito.grupo} › ${circuito.nombre}`);

  const classes = useStyles();
  const {
    tiposComprobante,
    comprobantes,
    loadingComprobantes,
    sucursales,
    sucursalesCargadas,
    loadingCambiarEmpresa,
    onLoadTiposComprobante,
    onLoadComprobantes,
    onLoadSucursales,
    onClear
  } = props;

  const [filtros, setFiltros] = useState(filtrosIniciales);
  const cambiandoEmpresa = useRef(false);

  useEffect(() => {
    // El listado del store puede ser del otro circuito (o de Pedidos): se limpia al entrar.
    onClear();
    onLoadTiposComprobante(origen);
    if (!sucursalesCargadas) onLoadSucursales();
    return () => onClear();
  }, []);

  // Al terminar el cambio de empresa se recargan los tipos de comprobante y se limpia el listado.
  useEffect(() => {
    if (loadingCambiarEmpresa) {
      cambiandoEmpresa.current = true;
    } else if (cambiandoEmpresa.current) {
      cambiandoEmpresa.current = false;
      onClear();
      setFiltros(filtrosIniciales());
      onLoadTiposComprobante(origen);
      onLoadSucursales();
    }
  }, [loadingCambiarEmpresa]);

  const cambiarFiltro = (campo) => (event) => setFiltros({ ...filtros, [campo]: event.target.value });

  const buscar = () => {
    onLoadComprobantes({
      idSistema: origen,
      fechaDesde: filtros.fechaDesde.format('YYYY-MM-DD'),
      fechaHasta: filtros.fechaHasta.format('YYYY-MM-DD'),
      cliente: filtros.cliente.trim(),
      idComproba: filtros.idComproba,
      sucursalId: filtros.sucursalId,
      nroExterno: filtros.nroExterno.trim()
    });
  };

  const fechasValidas =
    filtros.fechaDesde && filtros.fechaHasta && filtros.fechaDesde.isValid() && filtros.fechaHasta.isValid() &&
    !filtros.fechaHasta.isBefore(filtros.fechaDesde, 'day');

  return (
    <ConteinerWithEjecutarPagina showMultiempresa isExecuted={false}>
      <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={'es'}>
        <Paper className={classes.filtros} elevation={0}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={6} sm={3} md={2}>
              <KeyboardDatePicker
                className={classes.campo}
                label="Desde"
                format="DD/MM/YYYY"
                autoOk
                variant="inline"
                value={filtros.fechaDesde}
                onChange={(fecha) => setFiltros({ ...filtros, fechaDesde: fecha })}
                invalidDateMessage="Fecha inválida"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <KeyboardDatePicker
                className={classes.campo}
                label="Hasta"
                format="DD/MM/YYYY"
                autoOk
                variant="inline"
                value={filtros.fechaHasta}
                onChange={(fecha) => setFiltros({ ...filtros, fechaHasta: fecha })}
                invalidDateMessage="Fecha inválida"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                className={classes.campo}
                label={`${circuito.tercero} (código o nombre)`}
                value={filtros.cliente}
                onChange={cambiarFiltro('cliente')}
                onKeyPress={(event) => event.key === 'Enter' && fechasValidas && buscar()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                className={classes.campo}
                label="Tipo de comprobante"
                value={filtros.idComproba}
                onChange={cambiarFiltro('idComproba')}
              >
                <MenuItem value="">Todos</MenuItem>
                {tiposComprobante.map((tipo) => (
                  <MenuItem key={tipo.idComproba} value={tipo.idComproba.trim()}>
                    {`${tipo.idComproba.trim()} - ${tipo.nombre ? tipo.nombre.trim() : ''}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1}>
              <TextField
                select
                className={classes.campo}
                label="Sucursal"
                value={filtros.sucursalId}
                onChange={cambiarFiltro('sucursalId')}
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales
                  .filter((sucursal) => !sucursal.inactiva)
                  .map((sucursal) => (
                    <MenuItem key={sucursal.id} value={sucursal.id}>
                      {sucursal.nombre}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                className={classes.campo}
                label="Núm. externo"
                value={filtros.nroExterno}
                onChange={cambiarFiltro('nroExterno')}
                onKeyPress={(event) => event.key === 'Enter' && fechasValidas && buscar()}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                className={classes.boton}
                color="primary"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={!fechasValidas || loadingComprobantes}
                onClick={buscar}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </MuiPickersUtilsProvider>

      <ComprobantesImpresionTable
        comprobantes={comprobantes}
        loading={loadingComprobantes}
        tituloTercero={circuito.tercero}
        renderEstado={(rowData) => <Estado comprobante={rowData} />}
      />
    </ConteinerWithEjecutarPagina>
  );
};

const mapStateToProps = (state) => {
  return {
    tiposComprobante: state.impresion.tiposComprobante.tiposComprobante,
    comprobantes: state.impresion.comprobantes.comprobantes,
    loadingComprobantes: state.impresion.comprobantes.loading,
    sucursales: state.sucursales.loadSucursales.sucursales,
    sucursalesCargadas: state.sucursales.loadSucursales.estaCargado,
    loadingCambiarEmpresa: state.multiempresa.loadingCambiarEmpresa
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onLoadTiposComprobante: (idSistema) => dispatch(actions.loadTiposComprobanteImpresion(idSistema)),
    onLoadComprobantes: (filtros) => dispatch(actions.loadComprobantesImpresion(filtros)),
    onLoadSucursales: () => dispatch(actions.loadSucursales(undefined, false)),
    onClear: () => dispatch(actions.clearImpresion())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Impresion);
